<template>
  <div ref="parentRow" class="autocomplete" :class="rowClass">
    <label v-if="label" :for="inputId">{{ label }}</label>
    <div class="autocomplete-wrap">
      <input
        :id="inputId"
        ref="input"
        v-model="internalValue"
        type="text"
        @keydown="onKey"
        @focus="inputFocus(true)"
        @blur="inputFocus(false, $event)"
        @change.stop="onChange"
      >
      <button
        v-if="showAutocompleteList"
        class="fa accept mini"
        @mousedown="didClickAccept"
      >
        
      </button>
      <ul
        v-if="showAutocompleteList"
        class="naked predictions menu"
        :class="{ 'has-label': !!label }"
      >
        <li
          v-for="(choice, i) in autocompleteList"
          :key="`${choice}-${i}`"
          :class="{ selected: selectedPrediction === i }"
          @mousedown="chooseAutocomplete(choice)"
        >
          {{ choice }}
        </li>
      </ul>
    </div>
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.autocomplete-wrap {
  position: relative;

  input {
    margin-right: 24px;
  }

  button.accept {
    padding: 0;
    width: 1.5em;
    display: inline-block;
    margin-left: -24px;
  }

  .predictions {
    position: absolute;
    z-index: 100;
    top: 100%;
    left: 0;
    width: max-content;
    min-width: 5ch;
    max-width: 242px;
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.5);
    overflow-y: auto;
    border-radius: var(--br);
    background: var(--app-bgcolor);
    background: var(--input-bgcolor);

    &.has-label {
      left: calc(80px + 0.5em);
    }
  }
}
</style>

<script>
let nextInputId = 1;

export default {
  name: 'AutocompleteRow',

  props: {
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    choices: {
      type: Array,
      default: () => [],
    },
    rowClass: {
      type: String,
      default: 'form-row',
    },
  },

  emits: ['update:modelValue', 'input', 'blur', 'change'],

  data() {
    return {
      inputId: `${this.label || 'autocomplete'}-input-${nextInputId++}`,
      internalValue: this.modelValue ?? this.value,
      showAutocompleteList: false,
      selectedPrediction: null,
    };
  },

  computed: {
    rankedChoices() {
      return this.choices.map(x => [x, this.stringSimilarity(this.internalValue, x)]);
    },

    autocompleteList() {
      let list = [...this.rankedChoices];
      list.sort((a, b) => {
        const x = a[1];
        const y = b[1];
        if (x < y) return 1;
        if (y < x) return -1;
        return 0;
      });

      if (list.length > 10) {
        list = list.slice(0, 10);
      }

      return list.map(x => x[0]);
    },
  },

  watch: {
    modelValue(newValue) {
      this.internalValue = newValue;
    },
    value(newValue) {
      if (this.modelValue === undefined) {
        this.internalValue = newValue;
      }
    },
  },

  methods: {
    blur() {
      this.$refs.input?.blur();
    },

    didBlur(event) {
      this.showAutocompleteList = false;
      this.$emit('blur', event);
    },

    didClickAccept() {
      this.chooseArbitraryContents();
      this.inputFocus(false);
    },

    inputFocus(didFocus, event) {
      event && event.stopPropagation();

      if (didFocus) {
        this.showAutocompleteList = true;
        return;
      }

      this.didBlur(event);
    },

    onKey(e) {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (this.selectedPrediction == null || this.selectedPrediction < 1) return;
          this.selectedPrediction--;
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (this.selectedPrediction == null) {
            this.selectedPrediction = 0;
            return;
          }

          if (this.selectedPrediction >= this.autocompleteList.length - 1) return;
          this.selectedPrediction++;
          break;

        case 'Enter':
          e.preventDefault();
          if (e.altKey) {
            this.chooseArbitraryContents();
          }
          else {
            const choice = this.autocompleteList[this.selectedPrediction ?? 0];
            this.chooseAutocomplete(choice);
          }
          break;

        default:
          this.selectedPrediction = 0;
          this.showAutocompleteList = true;
      }
    },

    onChange(e) {
      e.stopPropagation();
    },

    chooseArbitraryContents() {
      this.chooseAutocomplete(this.internalValue);
    },

    chooseAutocomplete(value) {
      this.internalValue = value;
      this.$emit('update:modelValue', value);
      this.$emit('input', value);
      this.$emit('change', value);
      this.$refs.input?.dispatchEvent(new Event('change', { bubbles: true }));
      this.showAutocompleteList = false;
    },

    stringSimilarity(str1, str2, gramSize = 2) {
      const a = String(str1 ?? '');
      const b = String(str2 ?? '');

      const getNGrams = (s, len) => {
        s = ' '.repeat(len - 1) + s.toLowerCase() + ' '.repeat(len - 1);
        let v = new Array(s.length - len + 1);
        for (let i = 0; i < v.length; i++) {
          v[i] = s.slice(i, i + len);
        }
        return v;
      };

      if (!a.length || !b.length) return 0.0;

      const s1 = a.length < b.length ? a : b;
      const s2 = a.length < b.length ? b : a;

      const pairs1 = getNGrams(s1, gramSize);
      const pairs2 = getNGrams(s2, gramSize);
      const set = new Set(pairs1);

      let total = pairs2.length;
      let hits = 0;
      for (const item of pairs2) {
        if (set.delete(item)) {
          hits++;
        }
      }
      return hits / total;
    },
  },
};
</script>
